import { useEffect, useRef, useState } from 'react'

interface UseTypewriterOptions {
  /** Characters revealed per tick */
  charsPerTick?: number
  /** Milliseconds between ticks */
  intervalMs?: number
  /** When true, skip animation and show full text immediately */
  skip?: boolean
}

export const useTypewriter = (
  text: string,
  { charsPerTick = 3, intervalMs = 36, skip = false }: UseTypewriterOptions = {},
): { text: string; isDone: boolean } => {
  const [displayedLength, setDisplayedLength] = useState(skip ? text.length : 0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const wasInitiallySkipped = useRef(skip)

  // Only jump to full text if skip was true from the very first render (history messages).
  // If animation already started (skip was initially false), let it finish naturally.
  useEffect(() => {
    if (skip && wasInitiallySkipped.current) {
      setDisplayedLength(text.length)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [skip, text.length])

  const shouldAnimate = !wasInitiallySkipped.current

  useEffect(() => {
    if (!shouldAnimate) return

    // If there's new text to reveal, start/continue the interval
    if (displayedLength < text.length && !intervalRef.current) {
      intervalRef.current = setInterval(() => {
        setDisplayedLength((prev) => {
          const next = Math.min(prev + charsPerTick, text.length)
          if (next >= text.length) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current)
              intervalRef.current = null
            }
          }
          return next
        })
      }, intervalMs)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [text.length, displayedLength, charsPerTick, intervalMs, shouldAnimate])

  const isDone = displayedLength >= text.length
  return { text: text.slice(0, displayedLength), isDone }
}
